// src/components/solution_sections/ChallengeSolutionSection.tsx
import React from 'react';
import { Card } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { AlertTriangle, Sparkles } from 'lucide-react';

// Define a more specific type for the data this component receives
interface ChallengeSolutionData {
  sectionTitle?: string;
  icon?: string;
  problem: {
    title: string;
    description: string;
  };
  solution: {
    title: string;
    description: string;
  };
}

interface ChallengeSolutionSectionProps {
  data: ChallengeSolutionData;
  id: string;
  headerBgColor?: string;
}

export const ChallengeSolutionSection = React.forwardRef<HTMLDivElement, ChallengeSolutionSectionProps>(({
  data,
  id,
  headerBgColor,
}, ref) => {

  if (!data) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <Card
      ref={ref}
      title={data.sectionTitle} // <-- Renders title directly from data
      icon={data.icon}
      id={id}
      headerBgColor={headerBgColor}
    >
      <motion.div
        className="flex flex-col lg:flex-row gap-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {data.problem && (
          <motion.div
            variants={itemVariants}
            initial={{ y: 20, opacity: 0, boxShadow: 'none' }}
            whileInView={{ y: 0, opacity: 1, transition: { ...itemVariants.visible.transition } }}
            whileHover={{ y: -4, x: -4, boxShadow: '8px 8px 0px 0px #ef4444' }}
            viewport={{ once: true, amount: 0.5 }}
            className="flex-1 bg-gradient-to-br from-rose-50 to-red-50 p-6 rounded-xl border-2 border-black"
          >
            <div className="flex items-center gap-3 mb-3">
              <motion.div
                animate={{ scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
              >
                <AlertTriangle size={24} className="text-red-500" />
              </motion.div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                {data.problem.title} {/* <-- Renders title directly from data */}
              </h3>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {data.problem.description} {/* <-- Renders description directly from data */}
            </p>
          </motion.div>
        )}

        {data.solution && (
          <motion.div
            variants={itemVariants}
            initial={{ y: 20, opacity: 0, boxShadow: 'none' }}
            whileInView={{ y: 0, opacity: 1, transition: { ...itemVariants.visible.transition, delay: 0.1 } }}
            whileHover={{ y: -4, x: -4, boxShadow: '8px 8px 0px 0px #10b981' }}
            viewport={{ once: true, amount: 0.5 }}
            className="flex-1 bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border-2 border-black"
          >
            <div className="flex items-center gap-3 mb-3">
              <motion.div
                animate={{ scale: [1, 1.2, 1], y: [0, -2, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
              >
                <Sparkles size={24} className="text-green-500" />
              </motion.div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                {data.solution.title} {/* <-- Renders title directly from data */}
              </h3>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {data.solution.description} {/* <-- Renders description directly from data */}
            </p>
          </motion.div>
        )}
      </motion.div>
    </Card>
  );
});

